import styled, { css } from 'styled-components';
import { orange, white } from '../../../../style/colors';

const center = css`display: flex; align-items: center; justify-content: center;`;

export const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(0, 0, 0, 0.5); backdrop-filter: blur(4px);
  ${center}; z-index: 2000;
  opacity: ${p => p.isVisible ? 1 : 0}; visibility: ${p => p.isVisible ? 'visible' : 'hidden'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

export const Modal = styled.div`
  background: #f0f0f0; border-radius: 16px; box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 95%; max-width: 1100px; max-height: 80vh; display: flex; flex-direction: column;
  transform: ${p => p.isVisible ? 'scale(1)' : 'scale(0.95)'};
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

export const Header = styled.div`
  padding: 20px 24px; border-bottom: 1px solid #e5e7eb;
  display: flex; align-items: center; justify-content: space-between;
  background: ${white};
  border-radius: 16px 16px 0 0;
`;

export const Title = styled.h2`
  margin: 0; font-size: 20px; font-weight: 700; color: #111827; ${center}; gap: 10px;
  i { color: ${orange}; font-size: 24px; }
`;

export const CloseButton = styled.button`
  background: none; border: none; cursor: pointer; padding: 8px; border-radius: 8px;
  color: #6b7280; transition: all 0.2s; ${center};
  &:hover { background: #f3f4f6; color: #111827; }
  i { font-size: 24px; }
`;

export const Content = styled.div`
  padding: 24px;
  flex: 1;
  overflow-y: auto;
  background: #f0f0f0;
`;

export const Instructions = styled.div`
  font-size: 16px;
  color: #374151;
  margin-bottom: 20px;
  font-weight: 500;
`;

export const DropdownRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: stretch;
  flex-wrap: wrap;
  
  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

export const DropdownContainer = styled.div`
  flex: 1;
  min-width: 140px;
  background: ${white};
  border: 1px solid #d1d5db;
  border-radius: 8px;
  position: relative;
  opacity: ${p => p.disabled ? 0.5 : 1};
  pointer-events: ${p => p.disabled ? 'none' : 'auto'};
  transition: all 0.2s;
  
  &:hover {
    border-color: ${p => p.disabled ? '#d1d5db' : '#9ca3af'};
  }
  
  &:focus-within {
    border-color: ${orange};
    box-shadow: 0 0 0 2px rgba(255, 165, 0, 0.15);
  }
`;

export const DropdownLabel = styled.div`
  position: absolute;
  top: 6px;
  left: 12px;
  font-size: 11px;
  color: #6b7280;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  pointer-events: none;
  z-index: 1;
`;

export const DropdownSelect = styled.select`
  width: 100%;
  padding: 28px 36px 10px 12px;
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  cursor: pointer;
  appearance: none;
  outline: none;
  
  &:disabled {
    cursor: not-allowed;
    color: #9ca3af;
  }
`;

export const DropdownIcon = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #6b7280;
  font-size: 18px;
`;

export const SearchButtonContainer = styled.div`
  display: flex;
  align-items: stretch;
`;

export const Footer = styled.div`
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  background: ${white};
  border-radius: 0 0 16px 16px;
`;

export const Button = styled.button`
  padding: 10px 20px; border-radius: 8px; font-size: 14px; font-weight: 600;
  cursor: pointer; transition: all 0.2s; ${center}; gap: 8px; border: none;
  i { font-size: 18px; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

export const CancelButton = styled(Button)`
  background: ${white}; border: 1.5px solid #e5e7eb; color: #374151;
  &:hover:not(:disabled) { background: #f9fafb; border-color: #d1d5db; }
`;

export const SearchButton = styled(Button)`
  background: linear-gradient(135deg, ${orange} 0%, #ff8c00 100%); color: ${white};
  box-shadow: 0 2px 8px rgba(255, 165, 0, 0.25);
  padding: 12px 28px;
  min-width: 100px;
  border-radius: 24px;
  &:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(255, 165, 0, 0.35); }
  &:active:not(:disabled) { transform: translateY(0); }
`;

export const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: ${white};
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

export const Stats = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 16px;
  padding: 12px;
  background: #fff5f0;
  border: 1px solid #ffe0cc;
  border-radius: 8px;
`;

export const Stat = styled.div`
  ${center};
  gap: 8px;
  font-size: 13px;
  i { color: ${orange}; font-size: 18px; }
  strong { color: #92400e; font-weight: 600; }
`;

export const ErrorMessage = styled.div`
  margin-top: 12px;
  padding: 12px 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  i { font-size: 18px; }
`;
